import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Skeleton,
  Button,
  Tooltip,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import moment from "moment";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

// Custom components
import Card from "components/card/Card";

// Assets
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import {
  NumberWithCommas,
  toReadableName,
  truncateString,
} from "../../../../utils";
import { FilterDefinition } from "hooks/useTable";
import Filter from "components/filters";

export default function ColumnsTable(props: {
  columnsData: any;
  tableData: any;
  isLoading: boolean;
  currentPageNumber: number;
  onPageNumberPress: (pageNumber: number) => void;
  totalPageCount: number;
  filters: FilterDefinition[];
  updateFilter: (key: string, value: any) => void;
}) {
  const startPage = Math.max(1, props.currentPageNumber - 5);
  const endPage = Math.min(props.totalPageCount, startPage + 9);

  const { columnsData, tableData, isLoading, filters, updateFilter } = props;

  const tableInstance = useTable(
    {
      columns: columnsData,
      data: tableData,
      initialState: { pageSize: 200 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 200;

  useEffect(() => {
    tableInstance.setPageSize(200);
  }, [tableData, columnsData, isLoading, tableInstance]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const handleButtonClick = (page: number) => {
    if (page < 1 || page > props.totalPageCount) return;
    props.onPageNumberPress(page);
  };

  const renderButtons = () => {
    let buttons = [];
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          alignItems={"center"}
          justifyContent="center"
          w={8}
          h={8}
          borderRadius={8}
          colorScheme={i === props.currentPageNumber ? "purple" : "gray"}
          onClick={() => handleButtonClick(i)}
        >
          {i}
        </Button>
      );
    }
    return buttons;
  };

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "scroll" }}
    >
      <Flex px="25px" mb="10px" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Campaigns
        </Text>
        {filters && (
          <Flex flexDir="row" alignItems={"flex-start"} gap={4}>
            {filters.map((filter, index) => {
              return (
                <Filter
                  updateFilter={(key, value) => {
                    updateFilter(key, value);
                  }}
                  key={(filter.filterKey as string) + index}
                  {...filter}
                />
              );
            })}
          </Flex>
        )}
      </Flex>
      <Table {...getTableProps()} variant="striped" color="gray.500" mb="24px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  pe="10px"
                  key={index}
                  borderColor={borderColor}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: "10px", lg: "12px" }}
                    color="gray.400"
                  >
                    {column.render("Header")}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <Tr
                {...row.getRowProps()}
                key={"campaign change log table" + index}
              >
                {row.cells.map((cell, index) => {
                  let data;
                  if (cell.column.Header === "NAME") {
                    data = (
                      <div
                        onClick={() => {
                          /* const campaign = props.tableData.find(
                            (c: Campaign) => {
                              return c.name === cell.value;
                            }
                          );

                          history.push("/campaign/" + campaign.id); */
                        }}
                      >
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                          {cell.value}
                        </Text>
                      </div>
                    );
                  } else if (cell.column.Header === "APP") {
                    data = (
                      <img
                        referrerPolicy="no-referrer"
                        style={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          borderRadius: 4,
                        }}
                        src={cell.value}
                        alt=""
                      />
                    );
                  } else if (cell.column.Header === "CAMPAIGN") {
                    data =
                      cell.value.length > 30 ? (
                        <Tooltip placement="top" label={cell.value}>
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                          >
                            {truncateString(cell.value)}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                          {cell.value}
                        </Text>
                      );
                  } else if (
                    cell.column.Header !== "FIELD" &&
                    //@ts-ignore
                    cell.row.original.field === "click_url"
                  ) {
                    data =
                      cell.value && cell.value.length > 30 ? (
                        <Tooltip placement="top" label={cell.value}>
                          <Text color={textColor} fontWeight="500">
                            {truncateString(cell.value)}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                          {cell.value}
                        </Text>
                      );
                  } else if (cell.column.Header === "FIELD") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {toReadableName(cell.value)}
                      </Text>
                    );
                  } else if (
                    //@ts-ignore
                    cell.row.original.field === "daily_budget" &&
                    (cell.column.Header === "BEFORE" ||
                      cell.column.Header === "AFTER")
                  ) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        $
                        {NumberWithCommas(
                          Math.floor(Number(cell.value)).toFixed(2)
                        )}
                      </Text>
                    );
                  } else if (
                    //@ts-ignore
                    cell.row.original.field === "cpi" &&
                    (cell.column.Header === "BEFORE" ||
                      cell.column.Header === "AFTER")
                  ) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ${NumberWithCommas(Number(cell.value).toFixed(2))}
                      </Text>
                    );
                  } else if (
                    //@ts-ignore
                    (cell.row.original.field === "daily_cap" ||
                      //@ts-ignore
                      cell.row.original.field === "budget_cap") &&
                    (cell.column.Header === "BEFORE" ||
                      cell.column.Header === "AFTER")
                  ) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ${Math.floor(Number(cell.value)).toString()}
                      </Text>
                    );
                  } else if (
                    //@ts-ignore
                    cell.row.original.field === "id" &&
                    (cell.column.Header === "BEFORE" ||
                      cell.column.Header === "AFTER")
                  ) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {cell.column.Header === "BEFORE" ? "-" : "Created"}
                      </Text>
                    );
                  } else if (
                    //@ts-ignore
                    cell.row.original.field === "campaign_status" &&
                    (cell.column.Header === "BEFORE" ||
                      cell.column.Header === "AFTER")
                  ) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {cell.value === "true" ? "Activated" : "Deactivated"}
                      </Text>
                    );
                  } else if (cell.column.Header === "DATE") {
                    data = (
                      <Flex flexDir={"column"} alignItems={"center"}>
                        {moment(cell.value)
                          .format("YYYY-MM-DD hh:mm:ss")
                          .split(" ")
                          .map((str) => {
                            return (
                              <Text
                                color={textColor}
                                fontSize="sm"
                                fontWeight="500"
                              >
                                {str}
                              </Text>
                            );
                          }) ?? "-"}
                      </Flex>
                    );
                  } else {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {cell.value ?? "-"}
                      </Text>
                    );
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize={{ sm: "14px" }}
                      maxH="30px !important"
                      py="8px"
                      minW={{ sm: "150px", md: "200px", lg: "2px" }}
                      borderColor="transparent"
                    >
                      <Skeleton
                        borderRadius={16}
                        endColor={"rgba(53,63,255,0.17)"}
                        isLoaded={!isLoading}
                      >
                        {data}
                      </Skeleton>
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Flex flex={1} justifyContent={"center"} gap={2}>
        <Flex flex={1} justifyContent={"center"} alignItems="center" gap={2}>
          {/* <Button
            disabled={
              props.totalPageCount === 1 || props.currentPageNumber === 1
            }
            onClick={() => handleButtonClick(1)}
          >
            <ArrowLeftIcon w={4} h={4} />
          </Button> */}
          <Button
            onClick={() => handleButtonClick(props.currentPageNumber - 1)}
            disabled={props.currentPageNumber === 1}
          >
            <ChevronLeftIcon w={8} h={8} />
          </Button>
          {renderButtons()}
          <Button
            onClick={() => handleButtonClick(props.currentPageNumber + 1)}
            disabled={props.totalPageCount <= props.currentPageNumber}
          >
            <ChevronRightIcon w={8} h={8} />
          </Button>
          {/* <Button
            disabled={props.totalPageCount === props.currentPageNumber}
            onClick={() => handleButtonClick(props.totalPageCount)}
          >
            <ArrowRightIcon w={4} h={4} />
          </Button> */}
        </Flex>
      </Flex>
    </Card>
  );
}
